import type { ComponentProps } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { OverflowProps, WidthProps } from 'styled-system';
import { overflow, backgroundColor as systemBackgroundColor, width } from 'styled-system';

import { useLoadingImageBackground } from '../../hooks/image';
import { useIsObserved } from '../../hooks/intersection-observer';
import type { BackgroundColorProps } from '../../theme/system-types';
import type {
  ObjectFitProps,
  TextIndentProps,
  WhiteSpaceProps,
} from '../../theme/system-utilities';
import { objectFit, textIndent, whiteSpace } from '../../theme/system-utilities';
import { getDimensions } from '../../utils/cdn/image';
import { captureException } from '../../utils/error-reporter';
import type { MatchesObject } from '../Media';
import { ThemedMedia } from '../Media';
import type { ImageSizes, LazyImageProps, ResponsiveLazyImageProps } from './types';
import { defaultPreloadImage, getCurrentPreloadId, getSourceFromRatio } from './utils';

const UnstyledImage = styled.img<
  BackgroundColorProps &
    WidthProps &
    TextIndentProps &
    WhiteSpaceProps &
    OverflowProps &
    ObjectFitProps
>`
  ${systemBackgroundColor};
  ${width};
  ${textIndent};
  ${whiteSpace};
  ${overflow};
  ${objectFit};
`;

const Image = (props: ComponentProps<typeof UnstyledImage>) => (
  <UnstyledImage
    textIndent="100%"
    whiteSpace="nowrap"
    overflow="hidden"
    objectFit="cover"
    {...props}
  />
);

const useLazyImage = (imageSizes: ImageSizes, preloadImage: LazyImageProps['preloadImage']) => {
  const [loaded, setLoaded] = useState(false);
  const preloadId = useRef(null);
  const [imageSrc, setImageSrc] = useState('');
  const [ref, isObserved] = useIsObserved();
  const { backgroundColor, onImageLoad } = useLoadingImageBackground(loaded);

  useEffect(() => {
    let isAlive = true;

    (async () => {
      if (isObserved) {
        const src = getSourceFromRatio(imageSizes);
        const currentPreloadId = getCurrentPreloadId(src);
        preloadId.current = currentPreloadId;

        try {
          const preloadedSrc = await preloadImage(src);

          if (isAlive && preloadId.current === currentPreloadId) {
            setLoaded(true);
            setImageSrc(preloadedSrc);
            preloadId.current = null;
            onImageLoad();
          }
        } catch (err) {
          captureException(`Failed to load image. ${err}`);
        }
      }
    })();

    return () => {
      if (preloadId.current) {
        preloadId.current = null;
      }
      isAlive = false;
    };
  }, [isObserved]);

  return { loaded, imageSrc, backgroundColor, isObserved, ref };
};

const LazyImage = ({
  thumbnail,
  x1,
  x2,
  x3,
  children,
  preloadImage = defaultPreloadImage,
  ...imgProps
}: LazyImageProps) => {
  const imageSizes = { thumbnail, x1, x2, x3 };
  const placeholderSize = getDimensions(x1);
  const { loaded, imageSrc, backgroundColor, isObserved, ref } = useLazyImage(
    imageSizes,
    preloadImage
  );

  if (children) {
    return children({
      loaded,
      image: imageSrc || null,
      intersected: isObserved,
      thumbnail,
      innerRef: ref,
    });
  }

  return (
    <Image
      {...(placeholderSize?.width ? { width: placeholderSize.width } : {})}
      {...imgProps}
      alt={imgProps.alt || ''}
      loading={isObserved ? 'eager' : 'lazy'}
      src={loaded ? imageSrc : thumbnail}
      ref={ref}
      backgroundColor={backgroundColor}
      {...(imgProps.alt ? { 'aria-live': 'polite', 'aria-busy': !loaded && isObserved } : {})}
    />
  );
};

const ResponsiveLazyImage = (props: ResponsiveLazyImageProps) => {
  const { x1, x2, x3, mobile, tablet, desktop, large_desktop, preloadImage, ...restProps } = props;
  if (x1 && x2 && x3) {
    // Support for old responsive image format
    // that was working only for dpr
    return <LazyImage x1={x1} x2={x2} x3={x3} preloadImage={preloadImage} {...restProps} />;
  } else if (mobile && tablet && desktop) {
    // New responsive image format that supports
    // both dpr and main breakpoints
    return (
      <ThemedMedia>
        {(matches: MatchesObject) => {
          const imageProps =
            matches.large_desktop && large_desktop
              ? large_desktop
              : matches.desktop
                ? desktop
                : matches.tablet
                  ? tablet
                  : mobile;

          return <LazyImage preloadImage={preloadImage} {...imageProps} {...restProps} />;
        }}
      </ThemedMedia>
    );
  } else {
    // eslint-disable-next-line no-console
    console.warn('<ResponsiveLazyImage /> is missing required props');

    return null;
  }
};

const ResponsiveLazyImageMemo = React.memo(ResponsiveLazyImage);

export { LazyImage, ResponsiveLazyImage, ResponsiveLazyImageMemo };
